import React from 'react'
import { useTranslation } from 'react-i18next'

import { Box, SxProps, Theme, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { GranularityToggle } from '../GranularityToggle'
import { WindowTooltipContent } from '../WindowTooltipContent/WindowTooltipContent'
import { CATALOGUE_WINDOWS, CatalogueGranularity, CatalogueViewMode, CatalogueWindow, catalogueWindowMonths } from '../helpers/catalogueUtils'
import { compactToggleGroupSx } from '../helpers/toggleStyles'

// Hoisted so the list/chart view switch passes a referentially stable `sx` (the
// styles never change). Mirrors `compactToggleGroupSx` but keeps the bold weight
// the view switch uses.
const viewModeToggleGroupSx: SxProps<Theme> = {
  '& .MuiToggleButton-root': {
    textTransform: 'capitalize',
    fontWeight: 'bold',
    fontSize: '0.75rem',
    px: 1.5,
    py: 0.25,
    minHeight: 28,
    lineHeight: 1.4,
    '@media (pointer: coarse)': { minHeight: 44 },
    '&.Mui-selected': {
      color: (theme) => theme.palette.primary.contrastText,
      backgroundColor: (theme) => theme.palette.primary.main,
      '&:hover': {
        backgroundColor: (theme) => theme.palette.primary.dark,
      },
    },
  },
}

export interface CatalogueControlsRowProps {
  viewMode: CatalogueViewMode
  onViewModeChange: (viewMode: CatalogueViewMode) => void
  granularity: CatalogueGranularity
  onGranularityChange: (granularity: CatalogueGranularity) => void
  // An event-type pick narrows the segment below event-category grouping.
  eventCategoryLocked: boolean
  window: CatalogueWindow
  onWindowChange: (window: CatalogueWindow) => void
}

/**
 * The catalogue's control row above the list / chart card: view switch and
 * "Group by" on the left, the window toggle on the right. The metric lens lives
 * on the list / chart card itself.
 */
export const CatalogueControlsRow: React.FC<CatalogueControlsRowProps> = React.memo(
  ({ viewMode, onViewModeChange, granularity, onGranularityChange, eventCategoryLocked, window, onWindowChange }) => {
    const { t } = useTranslation()

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <GRTooltip content={t('market-analytics:tooltip_view_mode')} styles={{ display: 'inline-flex' }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              size="small"
              onChange={(_, next) => next && onViewModeChange(next)}
              aria-label={t('market-analytics:catalogue_view_mode')}
              sx={viewModeToggleGroupSx}
            >
              <ToggleButton value="list">{t('market-analytics:catalogue_view_list')}</ToggleButton>
              <ToggleButton value="chart">{t('market-analytics:catalogue_view_chart')}</ToggleButton>
            </ToggleButtonGroup>
          </GRTooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GRTooltip content={t('market-analytics:tooltip_granularity')} styles={{ display: 'inline-flex' }}>
              <Typography
                component="span"
                sx={{
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: 'text.secondary',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  cursor: 'help',
                }}
              >
                {t('market-analytics:granularity_label')}
              </Typography>
            </GRTooltip>
            <GranularityToggle value={granularity} onChange={onGranularityChange} eventCategoryLocked={eventCategoryLocked} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <GRTooltip content={<WindowTooltipContent />} styles={{ display: 'inline-flex' }}>
            <ToggleButtonGroup
              value={window}
              exclusive
              size="small"
              onChange={(_, next) => next && onWindowChange(next)}
              aria-label={t('market-analytics:catalogue_window_label')}
              sx={compactToggleGroupSx}
            >
              {CATALOGUE_WINDOWS.map((win) => (
                <ToggleButton key={win} value={win}>
                  {t('market-analytics:catalogue_window_months', { months: catalogueWindowMonths(win) })}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </GRTooltip>
        </Box>
      </Box>
    )
  }
)

CatalogueControlsRow.displayName = 'CatalogueControlsRow'
